/**
 * Generic Interest Form for City Landing Pages
 *
 * Fully parameterized interest form component that works for any city.
 * Handles form submission, validation, and success state.
 *
 * Usage:
 *   import { CityApiClient } from '@core/services/apiClient';
 *   const client = new CityApiClient(cityConfig.api);
 *
 *   <InterestForm
 *     cityName="Houston"
 *     stateName="Texas"
 *     apiClient={client}
 *     title="Interested in a Community App for Houston?"
 *     description="We're gauging interest right now..."
 *     successMessage="We'll keep you updated on Houston platform launch."
 *     enableChampionProgram={true}
 *     enablePlanSelector={true}
 *   />
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { validateInterestForm, sanitizeInput } from '../utils/validators';
import { CityApiClient } from '../services/apiClient';

export interface InterestFormData {
  name: string;
  email: string;
  zip?: string;
  phone?: string;
  facebookGroups?: string;
  planInterest?: string;
  interested: boolean;
  isChampion: boolean;
  featurePreference?: string;
  comments?: string;
  ageConfirmation: boolean;
}

export interface InterestFormProps {
  // City identity
  cityName: string;
  stateName: string;

  // API client
  apiClient: CityApiClient;
  // Optional override submit handler (bypasses apiClient)
  onSubmit?: (data: InterestFormData) => Promise<{ success: boolean; error?: string; data?: any }>;

  // Content
  title?: string;
  description?: string;
  successMessage?: string;

  // Feature flags
  enableChampionProgram?: boolean; // Default: true
  enablePlanSelector?: boolean; // Default: true
  enableFacebookGroups?: boolean; // Default: true

  // Customization
  features?: Array<{ value: string; label: string }>; // Custom feature options
  plans?: Array<{ value: string; label: string }>; // Custom plan options

  // Links
  championApplicationUrl?: string; // Default: /apply-champion
  termsUrl?: string; // Default: /terms
  privacyUrl?: string; // Default: /privacy
}

export function InterestForm(props: InterestFormProps) {
  const {
    cityName,
    stateName,
    apiClient,
    onSubmit,
    title,
    description,
    successMessage,
    enableChampionProgram = true,
    enablePlanSelector = true,
    enableFacebookGroups = true,
    features,
    plans,
    championApplicationUrl = '/apply-champion',
    termsUrl = '/terms',
    privacyUrl = '/privacy',
  } = props;

  // Default title/description
  const formTitle = title || `Interested in a Community App for ${cityName}?`;
  const formDescription =
    description ||
    `We're gauging interest right now. Fill out this quick form to stay updated and let us know what matters most to you. No commitment, just exploring the possibility together.`;
  const formSuccessMessage =
    successMessage || `We'll keep you updated as we explore bringing this platform to ${cityName}.`;

  // Default features
  const defaultFeatures = [
    { value: 'polls', label: 'Community Polls & Voting' },
    { value: 'petitions', label: 'Petition Organization' },
    { value: 'group-buying', label: 'Group Purchasing & Economic Power' },
    { value: 'discussions', label: 'Secure Community Discussion' },
    { value: 'other', label: 'Other' },
  ];
  const featureOptions = features || defaultFeatures;

  // Default plans
  const defaultPlans = [
    { value: 'freemium', label: 'Freemium (Always Free)' },
    { value: 'freemium-active', label: 'Active Member ($3 one-time for voting)' },
    { value: 'pro-annual', label: 'Pro Annual ($30/year - Best Value)' },
    { value: 'pro-monthly', label: 'Pro Monthly ($10/month)' },
    { value: 'champion', label: 'Champion (Lifetime Free Pro)' },
  ];
  const planOptions = plans || defaultPlans;

  // Form state
  const [formData, setFormData] = useState<InterestFormData>({
    name: '',
    email: '',
    zip: '',
    phone: '',
    facebookGroups: '',
    planInterest: '',
    interested: false,
    isChampion: false,
    featurePreference: '',
    comments: '',
    ageConfirmation: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    // Validate form data
    const validation = validateInterestForm({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      zipCode: formData.zip,
      interests: formData.interested ? ['general'] : [],
    });

    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(', ');
      setError(errorMessages);
      return;
    }

    // Age confirmation check
    if (!formData.ageConfirmation) {
      setError('You must confirm you are 18+ and agree to terms');
      return;
    }

    setIsSubmitting(true);

    try {
      // Sanitize inputs
      const sanitizedData: InterestFormData = {
        ...formData,
        name: sanitizeInput(formData.name),
        email: sanitizeInput(formData.email),
        comments: formData.comments ? sanitizeInput(formData.comments) : '',
      };

      let success = false;
      let submitError: string | undefined;

      if (onSubmit) {
        const res = await onSubmit(sanitizedData);
        success = res.success;
        submitError = res.error;
      } else {
        const response = await apiClient.submitInterestForm(sanitizedData);
        success = response.success;
        submitError =
          (response as any).message && !response.success ? (response as any).message : undefined;
      }

      if (success) {
        setSubmitted(true);
      } else {
        setError(submitError || 'Submission failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <section id="interest-form" className="py-12 sm:py-16 md:py-20 bg-primary/5">
        <div className="container px-4 sm:px-6">
          <Card className="max-w-2xl mx-auto text-center">
            <CardHeader className="px-4 sm:px-6 pt-6 sm:pt-8">
              <CardTitle className="text-2xl sm:text-3xl md:text-4xl">Thank You!</CardTitle>
              <CardDescription className="text-base sm:text-lg mt-2">
                We've received your interest.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 px-4 sm:px-6 pb-6 sm:pb-8">
              <p className="text-sm sm:text-base text-muted-foreground">{formSuccessMessage}</p>
              {enableChampionProgram && formData.isChampion && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                  <p className="text-sm sm:text-base text-blue-900 font-medium">
                    📋 Next Step: Complete Your Champion Application
                  </p>
                  <p className="text-sm text-blue-800">
                    To be considered for the Community Champion program, please complete the full
                    application form.
                  </p>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => (window.location.href = championApplicationUrl)}
                    className="mt-2"
                  >
                    Complete Application →
                  </Button>
                </div>
              )}
              <p className="text-sm sm:text-base text-muted-foreground">
                In the meantime, feel free to share this page with your neighbors. The more interest
                we see, the faster we can make this happen.
              </p>
              <div className="flex gap-3 sm:gap-4 justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={() => setSubmitted(false)}
                  className="text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-2.5"
                >
                  Submit Another Response
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  // Form state
  return (
    <section id="interest-form" className="py-12 sm:py-16 md:py-20 bg-primary/5">
      <div className="container px-4 sm:px-6">
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="px-4 sm:px-6 md:px-8 pt-6 sm:pt-8">
            <CardTitle className="text-2xl sm:text-3xl md:text-4xl text-center leading-tight">
              {formTitle}
            </CardTitle>
            <CardDescription className="text-center text-sm sm:text-base md:text-lg mt-3 sm:mt-4">
              {formDescription}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 md:px-8 pb-6 sm:pb-8">
            {/* Error Display */}
            {error && (
              <Alert variant="destructive" className="mb-4 sm:mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm sm:text-base">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  placeholder="Your full name"
                  required
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="text-base h-11"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm sm:text-base">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  required
                  value={formData.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="text-base h-11"
                />
              </div>

              {/* Plan Interest */}
              {enablePlanSelector && (
                <div className="space-y-2">
                  <Label htmlFor="planInterest" className="text-sm sm:text-base">
                    Which plan interests you? (Optional)
                  </Label>
                  <div className="h-11">
                    <Select
                      value={formData.planInterest}
                      onValueChange={(value: string) =>
                        setFormData({ ...formData, planInterest: value })
                      }
                    >
                      <SelectTrigger id="planInterest" className="text-base h-11">
                        <SelectValue placeholder="Select a plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {planOptions.map((plan) => (
                          <SelectItem
                            key={plan.value}
                            value={plan.value}
                            className="text-sm sm:text-base"
                          >
                            {plan.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    This helps us understand what pricing tier resonates with {cityName} residents.
                  </p>
                </div>
              )}

              {/* Zip Code */}
              <div className="space-y-2">
                <Label htmlFor="zip" className="text-sm sm:text-base">
                  Zip Code (Optional)
                </Label>
                <Input
                  id="zip"
                  placeholder="12345"
                  maxLength={5}
                  value={formData.zip}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, zip: e.target.value })
                  }
                  className="text-base h-11"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-sm sm:text-base">
                  Phone Number (Optional)
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 555-1234"
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="text-base h-11"
                />
              </div>

              {/* Facebook Groups */}
              {enableFacebookGroups && (
                <div className="space-y-2">
                  <Label htmlFor="facebookGroups" className="text-sm sm:text-base">
                    Are you active in any {cityName} Facebook groups? (Optional)
                  </Label>
                  <Input
                    id="facebookGroups"
                    placeholder={`e.g., ${cityName} Community Watch, ${cityName} Neighbors`}
                    value={formData.facebookGroups}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, facebookGroups: e.target.value })
                    }
                    className="text-base h-11"
                  />
                </div>
              )}

              {/* Interest Confirmation */}
              <div className="flex items-center space-x-3 min-h-[44px] py-2">
                <Checkbox
                  id="interested"
                  required
                  checked={formData.interested}
                  onCheckedChange={(checked: boolean) =>
                    setFormData({ ...formData, interested: checked })
                  }
                  className="w-5 h-5"
                />
                <Label
                  htmlFor="interested"
                  className="text-sm sm:text-base leading-relaxed cursor-pointer"
                >
                  Yes, I'm interested in learning more about a community platform for {cityName}.
                </Label>
              </div>

              {/* Champion Interest */}
              {enableChampionProgram && (
                <div className="p-3 sm:p-4 bg-accent/10 rounded-lg space-y-3">
                  <div className="flex items-center space-x-3 min-h-[44px]">
                    <Checkbox
                      id="champion"
                      checked={formData.isChampion}
                      onCheckedChange={(checked: boolean) =>
                        setFormData({ ...formData, isChampion: checked })
                      }
                      className="w-5 h-5"
                    />
                    <Label
                      htmlFor="champion"
                      className="text-sm sm:text-base leading-relaxed cursor-pointer font-medium"
                    >
                      I'm interested in becoming a Community Champion.
                    </Label>
                  </div>
                </div>
              )}

              {/* Feature Preference */}
              <div className="space-y-2">
                <Label htmlFor="feature" className="text-sm sm:text-base">
                  Most important feature? (Optional)
                </Label>
                <Select
                  value={formData.featurePreference}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, featurePreference: value })
                  }
                >
                  <SelectTrigger id="feature" className="text-base h-11">
                    <SelectValue placeholder="Select a feature" />
                  </SelectTrigger>
                  <SelectContent>
                    {featureOptions.map((feature) => (
                      <SelectItem
                        key={feature.value}
                        value={feature.value}
                        className="text-sm sm:text-base"
                      >
                        {feature.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Comments */}
              <div className="space-y-2">
                <Label htmlFor="comments" className="text-sm sm:text-base">
                  Anything else you'd like to share? (Optional)
                </Label>
                <Textarea
                  id="comments"
                  placeholder="Share your thoughts..."
                  rows={4}
                  value={formData.comments}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFormData({ ...formData, comments: e.target.value })
                  }
                  className="text-sm sm:text-base resize-none"
                />
              </div>

              {/* Age Confirmation */}
              <div className="flex items-center space-x-3 p-3 sm:p-4 bg-muted/30 rounded-lg border min-h-[44px]">
                <Checkbox
                  id="ageConfirmation"
                  required
                  checked={formData.ageConfirmation}
                  onCheckedChange={(checked: boolean) =>
                    setFormData({ ...formData, ageConfirmation: checked })
                  }
                  className="w-5 h-5 flex-shrink-0"
                />
                <Label
                  htmlFor="ageConfirmation"
                  className="text-xs sm:text-sm leading-relaxed cursor-pointer"
                >
                  I confirm that I am at least 18 years old and agree to the{' '}
                  <a
                    href={termsUrl}
                    target="_blank"
                    className="text-primary hover:underline font-medium"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href={privacyUrl}
                    target="_blank"
                    className="text-primary hover:underline font-medium"
                  >
                    Privacy Policy
                  </a>
                  .
                </Label>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                size="lg"
                className="w-full text-sm sm:text-base md:text-lg py-5 sm:py-6 font-semibold"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Yes, Keep Me Updated!'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
